import axios from 'axios'
import { deleteCookie, getCookie } from './utils'
import configValues from './config'

let sessionAuthorizationToken = null

export const resolveApiBaseURL = () => {
  const envBaseURL = process.env.REACT_APP_API_BASE_URL
  if (envBaseURL && typeof envBaseURL === 'string') {
    return envBaseURL.trim().replace(/\/+$/, '')
  }

  const host = window.location.hostname || ''

  if (
    host === 'localhost' ||
    host === '127.0.0.1' ||
    host.endsWith('.localhost')
  ) {
    return 'http://crate-core.test'
  }

  if (
    host.includes('user-qa.') ||
    host.includes('staging.')
  ) {
    return 'https://rest-qa.productioncrate.com'
  }

  return 'https://rest.productioncrate.com'
}

const instance = axios.create({
  baseURL: resolveApiBaseURL(),
  withCredentials: false,
})

export const setSessionAuthorizationToken = (token) => {
  sessionAuthorizationToken = token || null

  if (sessionAuthorizationToken) {
    instance.defaults.headers.common.Authorization = `Bearer ${sessionAuthorizationToken}`
    return
  }

  delete instance.defaults.headers.common.Authorization
}

export const clearSessionAuthorizationToken = () => {
  setSessionAuthorizationToken(null)
}

instance.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest'
instance.defaults.headers.common['Content-Type'] = 'application/json'
//instance.defaults.headers.common['Access-Control-Allow-Origin'] = '*';
//instance.defaults.mode = 'no-cors';

// Add JWT token if present in local storage
instance.interceptors.request.use(function (config) {
  //DEPRACATED LOCALSTORAGE
  //const token = localStorage.getItem(configValues.sessionTokenLocalStorageKey) || getCookie(configValues.sessionTokenCookieKey);

  const token = sessionAuthorizationToken || getCookie(configValues.sessionTokenCookieKey)

  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  } else if (config.headers?.Authorization) {
    delete config.headers.Authorization
  }

  return config
})

if (!window.requestStack) {
  window.requestStack = []
}

const rejectQueuedRequests = (errorPayload) => {
  try {
    const queuedRequests = Array.isArray(window.requestStack) ? [...window.requestStack] : [];
    window.requestStack = [];
    queuedRequests.forEach((request) => {
      if (request?.promiseReject) {
        request.promiseReject(errorPayload);
      }
    });
  } catch (error) {
    console.log('Error while rejecting queued requests', error);
  }
};

const handleSeatRevoked = (errorPayload) => {
  try {
    if (Array.isArray(window.requestStack)) {
      window.requestStack.forEach((request) => {
        if (request?.promiseReject) {
          request.promiseReject(errorPayload)
        }
      })
      window.requestStack = []
    }
  } catch (error) {
    console.log('Error while clearing request stack after seat revocation', error)
  }

  deleteCookie(configValues.sessionTokenCookieKey)
  deleteCookie(configValues.refreshTokenCookieKey)
  localStorage.removeItem(configValues.sessionTokenLocalStorageKey)
  localStorage.removeItem(configValues.refreshTokenLocalStorageKey)
  clearSessionAuthorizationToken()
  delete axios.defaults.headers.common['Authorization']

  if (window.__seatRevokedRedirecting) {
    return
  }

  window.__seatRevokedRedirecting = true
  const seatRevokedLoginUrl = '/login?reason=seat-revoked'
  if (window.location.pathname !== '/login' || window.location.search !== '?reason=seat-revoked') {
    window.location.href = seatRevokedLoginUrl
  }
}

instance.interceptors.response.use(response => {
  return response
}, async error => {
  const originalRequest = error.config
  if (error && error.response && error.response.status && error.response?.status === 401) {
    const responseData = error?.response?.data
    if (responseData?.error === 'Seat revoked') {
      handleSeatRevoked(responseData)
      return Promise.reject(responseData)
    }

    if (originalRequest?.url?.includes('login') || originalRequest?.url?.includes('register')) {
      if (error.response.data) {
        //console.log("401 error", error.response.data);
        return Promise.reject(error.response.data)
      } else {
        //console.log("401 error", {msg: "no error message from api"});
        return Promise.reject('no error message from api')
      }
    }
    if (originalRequest?.url?.includes('refresh')) {

      return false
    }

    if (originalRequest._retry) {
      //place your reentry code
      return Promise.reject(error.response.data ? error.response.data : 'no error message from api')
    }

    originalRequest._retry = true

    const requestPromise = new Promise((resolve, reject) => {
      window.requestStack.push({
        url: originalRequest.url,
        request: () => instance(originalRequest),
        promiseResolve: resolve,
        promiseReject: reject,
      });
    });

    if (window.refreshToken && window.requestStack.length === 1) {
      //console.log("refreshing token");
      window.refreshToken()
        .then((new_access_token) => {
          if (!new_access_token) {
            rejectQueuedRequests(error.response?.data || "Unauthorized");
            return;
          }
          //console.log("NEW TOKEN", new_access_token);
          setSessionAuthorizationToken(new_access_token);
          axios.defaults.headers.common['Authorization'] = 'Bearer ' + new_access_token;
          const queuedRequests = Array.isArray(window.requestStack) ? [...window.requestStack] : [];
          window.requestStack = [];
          queuedRequests.forEach(req => {
            //console.log("resending request", req.url);
            req.promiseResolve(req.request())
          })
        })
        .catch(e => {
          console.log("couldn't refresh token...", e);
          rejectQueuedRequests(error.response?.data || e || "Unauthorized");
        });
    } else if (window.requestStack.length === 1) {
      rejectQueuedRequests(error.response?.data || "Unauthorized");
    }

    return await requestPromise

  } else if (error && error.response && error.response.status && error.response?.status === 422) {
    if (error.response.data) {
      //console.log("401 error", error.response.data);
      return Promise.reject(error.response.data)
    } else {
      //console.log("401 error", {msg: "no error message from api"});
      return Promise.reject('no error message from api')
    }
  } else {
    return Promise.reject(error)
  }

})

export default instance
